import { useEffect, useState } from 'react';
import { Loader2, Receipt, Percent, CheckCircle2, TrendingUp } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';

const STATUS_STYLES = {
  paid: 'bg-emerald-100 text-emerald-800',
  open: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  void: 'bg-slate-200 text-slate-500',
};

/**
 * What the platform charges the operator, from the operator's side.
 *
 * Read-only on the rate — an owner who could change the rate they pay is not a
 * rate — but they can record having paid a statement, because they are the one
 * sending the money and they should not have to ask someone else to reflect
 * that. Who asserted payment is recorded either way.
 */
export default function PlatformChargesPage() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [overview, config] = await Promise.all([api.platformOverview(), api.platformSettings()]);
      setData(overview);
      setSettings(config.settings);
    } catch (err) {
      setError(err.message);
      setData({ failed: true });
    }
  };

  useEffect(() => { load(); }, []);

  if (data === null) {
    return (
      <div className="grid place-items-center py-20" role="status" aria-label="Loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }
  if (data.failed) {
    return <p role="alert" className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  const { current_period: current, invoices } = data;
  const owed = invoices.filter((i) => i.status === 'open')
    .reduce((sum, i) => sum + Number(i.balance_cents), 0);

  const pay = async (invoice) => {
    const note = window.prompt(
      `Record ${invoice.invoice_number} (${formatCents(invoice.total_cents)}) as paid?\n\n`
      + 'Reference — the bank transfer id or cheque number you sent:');
    if (note === null) return;
    setBusy(invoice.public_id); setError(null);
    try {
      await api.markPlatformInvoicePaid(invoice.public_id, note);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Platform charges</h1>
      <p className="mt-1 text-sm text-slate-600">
        What the platform charges on card payments your tenants make, billed monthly.
      </p>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Stat Icon={Percent} label="Rate" value={`${(settings.commission_bps / 100).toFixed(2)}%`}
              hint={`Net ${settings.payment_terms_days} days`} />
        <Stat Icon={TrendingUp} label="Accruing now" value={formatCents(current.fee_cents)}
              hint={`${current.transaction_count} payment${current.transaction_count === 1 ? '' : 's'} since the last bill`} />
        <Stat Icon={Receipt} label="You owe" value={formatCents(owed)}
              hint={owed > 0 ? 'Across open statements' : 'Nothing outstanding'}
              tone={owed > 0 ? 'bad' : 'good'} />
      </div>

      <h2 className="mt-8 text-sm font-bold text-slate-900">Statements</h2>
      {invoices.length === 0 ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white py-14 text-center">
          <Receipt className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">Nothing billed yet</p>
          <p className="mt-1 text-sm text-slate-500">
            The first statement arrives after a month of card payments.
          </p>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {invoices.map((i) => (
            <li key={i.public_id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  {i.invoice_number}
                  <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${
                    STATUS_STYLES[i.status] ?? STATUS_STYLES.void}`}>
                    {i.status}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {formatDate(i.period_start)} · {i.transaction_count} payment
                  {i.transaction_count === 1 ? '' : 's'} totalling {formatCents(i.gross_cents)}
                </p>
                <p className="text-xs text-slate-500">
                  Due {formatDate(i.due_on)}
                  {i.status === 'open' && Number(i.days_overdue) > 0 && (
                    <span className="font-bold text-rose-700"> · {i.days_overdue} days late</span>
                  )}
                  {i.payment_note && <span className="block text-slate-400">{i.payment_note}</span>}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <p className="text-lg font-extrabold tabular-nums text-slate-900">
                  {formatCents(i.total_cents)}
                </p>
                {i.status === 'open' ? (
                  <button type="button" onClick={() => pay(i)} disabled={busy === i.public_id}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700 disabled:opacity-50">
                    {busy === i.public_id && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                    Mark paid
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    {i.paid_at ? formatDate(String(i.paid_at).slice(0, 10)) : 'Settled'}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-xs text-slate-500">
        Marking a statement paid records that you have sent payment, with whatever reference you
        give. The rate is set by the platform and cannot be changed here.
      </p>
    </div>
  );
}

function Stat({ Icon, label, value, hint, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <Icon className="h-5 w-5 text-brand-600" aria-hidden="true" />
      <p className={`mt-3 text-2xl font-extrabold tabular-nums ${
        tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-[12px] text-slate-400">{hint}</p>
    </div>
  );
}
